class LuxuryMosaic extends HTMLElement {
  connectedCallback() {
    this.autoplay = this.dataset.autoplay === 'true';
    this.intervalMs = Number(this.dataset.interval || 7) * 1000;
    this.desktopTimer = null;
    this.desktopIndex = 0;
    this.mobileIndex = 0;
    this.isInView = false;
    this.entranceTimer = null;

    this.desktopRoot = this.querySelector('[data-lm-desktop]');
    this.desktopPages = Array.from(this.querySelectorAll('[data-lm-page]'));
    this.desktopDotsWrap = this.querySelector('[data-lm-desktop-dots]');
    this.mobileTrack = this.querySelector('[data-lm-mobile-track]');
    this.mobileSlides = Array.from(this.querySelectorAll('[data-lm-mobile-slide]'));
    this.mobileDotsWrap = this.querySelector('[data-lm-mobile-dots]');

    this.onPrev = () => this.goDesktop(this.desktopIndex - 1);
    this.onNext = () => this.goDesktop(this.desktopIndex + 1);
    this.onMobileScroll = this.handleMobileScroll.bind(this);
    this.onVisibility = this.handleVisibility.bind(this);
    this.pauseAutoplay = () => this.stopAutoplay();
    this.resumeAutoplay = () => {
      if (this.isInView) this.startAutoplay();
    };

    this.querySelector('[data-lm-prev]')?.addEventListener('click', this.onPrev);
    this.querySelector('[data-lm-next]')?.addEventListener('click', this.onNext);
    this.mobileTrack?.addEventListener('scroll', this.onMobileScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    this.desktopRoot?.addEventListener('mouseenter', this.pauseAutoplay);
    this.desktopRoot?.addEventListener('mouseleave', this.resumeAutoplay);

    this.buildDots(this.desktopDotsWrap, this.desktopPages.length, (index) => this.goDesktop(index));
    this.buildDots(this.mobileDotsWrap, this.mobileSlides.length, (index) => this.goMobile(index));

    this.setupInViewObserver();

    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      window.requestAnimationFrame(() => this.playPageEntrance(0));
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.inViewObserver?.disconnect();
    if (this.entranceTimer) window.clearTimeout(this.entranceTimer);
    this.querySelector('[data-lm-prev]')?.removeEventListener('click', this.onPrev);
    this.querySelector('[data-lm-next]')?.removeEventListener('click', this.onNext);
    this.mobileTrack?.removeEventListener('scroll', this.onMobileScroll);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.desktopRoot?.removeEventListener('mouseenter', this.pauseAutoplay);
    this.desktopRoot?.removeEventListener('mouseleave', this.resumeAutoplay);
  }

  setupInViewObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      return;
    }

    this.inViewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasInView = this.isInView;
          this.isInView = entry.isIntersecting;

          if (entry.isIntersecting && !wasInView) {
            this.playPageEntrance(this.desktopIndex);
          }

          if (!entry.isIntersecting && wasInView) {
            this.stopAutoplay();
          }
        });
      },
      { threshold: 0.25 }
    );

    this.inViewObserver.observe(this);
  }

  buildDots(wrapper, count, onSelect) {
    if (!wrapper || count <= 1) return;
    wrapper.replaceChildren();

    for (let i = 0; i < count; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `luxury-mosaic__dot${i === 0 ? ' is-active' : ''}`;
      button.setAttribute('aria-label', `Go to slide ${i + 1}`);
      button.addEventListener('click', () => onSelect(i));
      wrapper.appendChild(button);
    }
  }

  setActiveDots(wrapper, index) {
    if (!wrapper) return;
    wrapper.querySelectorAll('.luxury-mosaic__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  forceReflow(el) {
    void el.offsetHeight;
  }

  beginEntrance(page) {
    page.classList.add('is-replaying', 'is-active');
    this.forceReflow(page);

    // Double rAF: paint off-screen state, then animate in
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        page.classList.remove('is-replaying');
      });
    });
  }

  playPageEntrance(index) {
    if (!this.desktopPages.length) return;

    const next = (index + this.desktopPages.length) % this.desktopPages.length;
    const target = this.desktopPages[next];

    if (this.entranceTimer) {
      window.clearTimeout(this.entranceTimer);
      this.entranceTimer = null;
    }

    if (!target) return;

    if (target.classList.contains('is-active')) {
      this.beginEntrance(target);
      this.desktopIndex = next;
      this.setActiveDots(this.desktopDotsWrap, next);
      this.startAutoplay();
      return;
    }

    target.setAttribute('aria-hidden', 'false');
    this.beginEntrance(target);

    this.desktopPages.forEach((page, i) => {
      if (i === next) return;
      page.classList.remove('is-active', 'is-replaying');
      page.setAttribute('aria-hidden', 'true');
    });

    this.desktopIndex = next;
    this.setActiveDots(this.desktopDotsWrap, next);
    this.startAutoplay();
  }

  goDesktop(index) {
    if (!this.desktopPages.length) return;
    const next = (index + this.desktopPages.length) % this.desktopPages.length;
    this.playPageEntrance(next);
  }

  goMobile(index) {
    if (!this.mobileTrack || !this.mobileSlides.length) return;
    const next = Math.max(0, Math.min(index, this.mobileSlides.length - 1));
    const slide = this.mobileSlides[next];
    this.mobileTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.mobileIndex = next;
    this.setActiveDots(this.mobileDotsWrap, next);
  }

  handleMobileScroll() {
    if (!this.mobileTrack || !this.mobileSlides.length) return;
    const width = this.mobileTrack.clientWidth || 1;
    const index = Math.round(this.mobileTrack.scrollLeft / width);
    if (index === this.mobileIndex) return;
    this.mobileIndex = index;
    this.setActiveDots(this.mobileDotsWrap, index);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplay || this.desktopPages.length <= 1 || !this.isInView || document.hidden) return;
    this.desktopTimer = window.setInterval(() => this.goDesktop(this.desktopIndex + 1), this.intervalMs);
  }

  stopAutoplay() {
    if (!this.desktopTimer) return;
    window.clearInterval(this.desktopTimer);
    this.desktopTimer = null;
  }

  handleVisibility() {
    if (document.hidden) {
      this.stopAutoplay();
      return;
    }

    if (this.isInView) {
      this.playPageEntrance(this.desktopIndex);
      this.startAutoplay();
    }
  }
}

if (!customElements.get('luxury-mosaic')) {
  customElements.define('luxury-mosaic', LuxuryMosaic);
}
